/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Imgix from 'js/components/Imgix';
import { useRetracked } from 'js/lib/retracked';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { logo } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import { clearUpgradeData, getUpgradeData } from 'bundles/coursera-plus-common/utils/plusMonthlyToAnnualUpgradeUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/my-purchases-graphql';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles: Record<string, Interpolation<Theme>> = {
  topLogo: css`
    margin-bottom: 24px;
    margin-top: 8px;
    padding-left: 48px;
  `,
  list: css`
    list-style: initial;
    padding-left: 20px;
  `,
  listHead: css`
    font-size: 16px;
  `,
  actions: css`
    padding-top: 16px;
  `,
};

const AnnualUpgradeConfirmationModal = () => {
  const [isOpen, setIsOpen] = useState(true);
  const retrackedTrack = useRetracked();

  const { Content, HeadingGroup, Actions } = Dialog;

  const handleCloseModal = (subscriptionId?: string, userId?: number) => {
    clearUpgradeData();
    retrackedTrack({
      trackingData: { courseraPlusSubscriptionId: subscriptionId, userId },
      trackingName: `annual_upgrade_confirmation_modal_close`,
      action: 'click',
    });
    setIsOpen(false);
  };

  const { nextBillingDate, subscriptionId, userId } = getUpgradeData() ?? {};

  if (!nextBillingDate) {
    return null;
  }

  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      width="medium"
      onClose={() => {
        handleCloseModal(subscriptionId, userId);
      }}
      data-testid="annual-upgrade-confirmation-modal"
    >
      <Imgix src={logo.PLUS_FULL_BLUE} alt={_t('Coursera Plus logo')} maxWidth={200} css={styles.topLogo} />
      <HeadingGroup>{_t(`You've switched to Coursera Plus annual 🎉`)}</HeadingGroup>
      <Content>
        <TrackedDiv
          withVisibilityTracking={true}
          trackingName="annual_upgrade_confirmation_modal"
          trackingData={{ courseraPlusSubscriptionId: subscriptionId, userId }}
        >
          <>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.listHead}>
              {_t('What happens next')}
            </Typography2>
            <ul css={styles.list}>
              <li>
                <Typography2 component="p">
                  {_t(
                    "You'll be charged for your Coursera Plus annual subscription on #{nextBillingDate} and annually thereafter.",
                    {
                      nextBillingDate,
                    }
                  )}
                </Typography2>
              </li>
              <li>
                <Typography2 component="p">
                  {_t(`We'll send you an email confirmation with details about your updated subscription.`)}
                </Typography2>
              </li>
            </ul>
          </>
        </TrackedDiv>
      </Content>
      <Actions css={styles.actions}>
        <TrackedButton
          variant="primary"
          onClick={() => {
            handleCloseModal(subscriptionId, userId);
          }}
          trackingName="annual_uprade_confirmation_modal_continue_button"
        >
          {_t('Continue')}
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default AnnualUpgradeConfirmationModal;
